import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Users, CreditCard, FileCheck, Wallet, Heart, Activity, ArrowRight, TrendingUp, MapPin, Building2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const COLORS = ['#1B7340', '#C8A951', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#10b981'];

export const AdminOverview = () => {
  const { adminApi } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const res = await adminApi.get('/admin/dashboard-stats');
      setStats(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-afriperx-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = stats ? [
    { label: 'Total Members', value: stats.total_members?.toLocaleString(), icon: Users, color: 'bg-emerald-50 text-emerald-600', link: '/admin/members' },
    { label: 'Bank Cards', value: stats.bank_cards?.toLocaleString(), icon: CreditCard, color: 'bg-sky-50 text-sky-600', link: null },
    { label: 'App. Approvals', value: stats.application_approvals?.toLocaleString(), icon: FileCheck, color: 'bg-violet-50 text-violet-600', link: null },
    { label: 'Loan Applications', value: stats.loan_applications?.toLocaleString(), icon: Wallet, color: 'bg-amber-50 text-amber-600', link: null },
    { label: 'Medical Aid Apps', value: stats.medical_aid_applications?.toLocaleString(), icon: Heart, color: 'bg-rose-50 text-rose-600', link: null },
    { label: 'Active (45 days)', value: stats.active_last_45_days?.toLocaleString(), icon: Activity, color: 'bg-green-50 text-green-600', link: null },
  ] : [];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div>
        <h1 className="font-syne text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-500 text-sm">Overview of AfriPerx platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map((stat, i) => {
          const Icon = stat.icon;
          const colorClasses = stat.color.split(' ');
          return (
            <Card
              key={i}
              className={`p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 ${stat.link ? 'cursor-pointer hover:border-afriperx-green/30' : ''}`}
              onClick={() => stat.link && navigate(stat.link)}
              data-testid={`stat-card-${stat.label.toLowerCase().replace(/[\s().]/g, '-')}`}
            >
              <div className={`w-9 h-9 rounded-lg ${colorClasses[0]} flex items-center justify-center mb-3`}>
                <Icon className={`w-4 h-4 ${colorClasses[1]}`} />
              </div>
              <p className="text-xl font-bold text-gray-900">{stat.value}</p>
              <div className="flex items-center justify-between">
                <p className="text-gray-400 text-xs">{stat.label}</p>
                {stat.link && <ArrowRight className="w-3 h-3 text-afriperx-green" />}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      {stats && (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Province Distribution */}
          <Card className="p-5 rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-afriperx-green" />
              <h2 className="font-syne font-bold text-gray-900 text-sm">Members by Province</h2>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.province_distribution} layout="vertical" margin={{ left: 70, right: 10 }}>
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={70} />
                  <Tooltip formatter={(val) => val.toLocaleString()} />
                  <Bar dataKey="count" fill="#1B7340" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Union Distribution */}
          <Card className="p-5 rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4 text-afriperx-gold" />
              <h2 className="font-syne font-bold text-gray-900 text-sm">Members by Union</h2>
            </div>
            <div className="h-64 flex items-center">
              <ResponsiveContainer width="50%" height="100%">
                <PieChart>
                  <Pie data={stats.union_distribution} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={40}>
                    {stats.union_distribution.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => val.toLocaleString()} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1 overflow-y-auto max-h-64 pl-2">
                {stats.union_distribution.map((u, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-gray-600 truncate">{u.name}</span>
                    <span className="ml-auto text-gray-900 font-medium">{u.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <Card className="p-5 rounded-xl border border-gray-100 shadow-sm">
        <h2 className="font-syne font-bold text-gray-900 text-sm mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button onClick={() => navigate('/admin/members')} className="p-4 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors text-left" data-testid="quick-action-members">
            <Users className="w-5 h-5 text-emerald-600 mb-2" />
            <p className="text-gray-900 font-medium text-sm">All Members</p>
            <p className="text-gray-400 text-xs">{stats?.total_members?.toLocaleString()} total</p>
          </button>
          <button onClick={() => navigate('/admin/applications')} className="p-4 bg-violet-50 rounded-xl hover:bg-violet-100 transition-colors text-left" data-testid="quick-action-applications">
            <FileCheck className="w-5 h-5 text-violet-600 mb-2" />
            <p className="text-gray-900 font-medium text-sm">Applications</p>
            <p className="text-gray-400 text-xs">{stats?.application_approvals?.toLocaleString()} approved</p>
          </button>
          <button onClick={() => navigate('/admin/loans')} className="p-4 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors text-left">
            <Wallet className="w-5 h-5 text-amber-600 mb-2" />
            <p className="text-gray-900 font-medium text-sm">Loans</p>
            <p className="text-gray-400 text-xs">{stats?.loan_applications?.toLocaleString()} applications</p>
          </button>
          <button onClick={() => navigate('/admin/analytics')} className="p-4 bg-sky-50 rounded-xl hover:bg-sky-100 transition-colors text-left" data-testid="quick-action-analytics">
            <TrendingUp className="w-5 h-5 text-sky-600 mb-2" />
            <p className="text-gray-900 font-medium text-sm">Analytics</p>
            <p className="text-gray-400 text-xs">View statistics</p>
          </button>
        </div>
      </Card>
    </div>
  );
};
